import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { message } from 'antd';
import CarouselsApi from 'libs/api/website/carousels';
import AuthService from 'services/Auth';

const useCarousels = () => {
  const [carousels, setCarousels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState({});
  const [sorting, setSorting] = useState({ field: '', order: '' });
  const [companies, setCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [shoppingCenters, setShoppingCenters] = useState([]);

  const navigate = useNavigate();

  const checkAuth = useCallback(() => {
    const token = AuthService.getToken();
    if (!token) {
      message.error('You are not authorized. Please login.');
      navigate('/login');
      return false;
    }
    return true;
  }, [navigate]);

  const fetchCarousels = useCallback(
    async (params = {}) => {
      if (!checkAuth()) return;

      setLoading(true);
      try {
        const response = await CarouselsApi.list(params);

        if (response.data.success) {
          setCarousels(response.data.carousels || []);
          setTotal(response.data.pagination?.total_count || 0);
        } else {
          setCarousels([]);
          setTotal(0);
        }
      } catch (error) {
        console.error('Error fetching carousels:', error);
        if (error.response?.status === 401) {
          message.error('You are not authorized to access this page.');
          navigate('/login');
        } else {
          message.error(
            error.response?.data?.message || 'Failed to load carousels',
          );
        }
      } finally {
        setLoading(false);
      }
    },
    [checkAuth, navigate],
  );

  const fetchCarousel = useCallback(
    async (id) => {
      if (!checkAuth()) return null;

      try {
        const response = await CarouselsApi.show(id);
        if (response.data.success) {
          return response.data.carousel;
        }
        return null;
      } catch (error) {
        console.error('Error fetching carousel:', error);
        if (error.response?.status === 401) {
          message.error('You are not authorized to access this page.');
          navigate('/login');
        } else {
          message.error(
            error.response?.data?.message || 'Failed to load carousel',
          );
        }
        return null;
      }
    },
    [checkAuth, navigate],
  );

  const createCarousel = useCallback(
    async (data) => {
      if (!checkAuth()) return null;

      try {
        const response = await CarouselsApi.create(data);
        if (response.data.success) {
          return response.data.success;
        }
        return null;
      } catch (error) {
        console.error('Error creating carousel:', error);
        if (error.response?.status === 401) {
          message.error('You are not authorized to access this page.');
          navigate('/login');
        } else {
          message.error(
            error.response?.data?.message || 'Failed to create carousel',
          );
        }
        return null;
      }
    },
    [checkAuth, navigate],
  );

  const updateCarousel = useCallback(
    async (id, data) => {
      if (!checkAuth()) return null;

      try {
        const response = await CarouselsApi.update(id, data);
        if (response.data.success) {
          return response.data.carousel;
        }
        return null;
      } catch (error) {
        console.error('Error updating carousel:', error);
        if (error.response?.status === 401) {
          message.error('You are not authorized to access this page.');
          navigate('/login');
        } else {
          message.error(
            error.response?.data?.message || 'Failed to update carousel',
          );
        }
        return null;
      }
    },
    [checkAuth, navigate],
  );

  const deleteCarousel = useCallback(
    async (id) => {
      if (!checkAuth()) return false;

      try {
        await CarouselsApi.destroy(id);
        message.success('Carousel deleted successfully');
        return true;
      } catch (error) {
        console.error('Error deleting carousel:', error);
        if (error.response?.status === 401) {
          message.error('You are not authorized to access this page.');
          navigate('/login');
        } else {
          message.error(
            error.response?.data?.message || 'Failed to delete carousel',
          );
        }
        return false;
      }
    },
    [checkAuth, navigate],
  );

  const fetchFilters = useCallback(async () => {
    if (!checkAuth()) return;
    setLoadingCompanies(true);
    try {
      const response = await CarouselsApi.getFilters();
      const companiesRaw = response.data.all_online_brand_companies || [];
      const companies = companiesRaw.map((c) => ({
        value: c.id || c.value,
        title: c.title || c.name || '',
        name: c.name || c.title || '',
        shopping_center: c.shopping_center || '',
        shopping_center_title: c.shopping_center_title || '',
      }));

      const shoppingCentersRaw = response.data.shopping_centers || [];
      setCompanies(companies);
      setShoppingCenters(shoppingCentersRaw);
    } catch (error) {
      console.error('Error fetching filters:', error);
      if (error.response?.status === 401) {
        message.error('You are not authorized to access this page.');
        navigate('/login');
      } else {
        message.error('Failed to load filters');
      }
    } finally {
      setLoadingCompanies(false);
    }
  }, [checkAuth, navigate]);

  useEffect(() => {
    fetchFilters();
  }, [fetchFilters]);

  useEffect(() => {
    const params = {
      page: currentPage,
      per: pageSize,
    };
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params[key] = value;
      }
    });
    if (sorting.order) {
      params.sortable_column = sorting.field;
      params.direction = sorting.order;
    }
    fetchCarousels(params);
  }, [currentPage, pageSize, filters, sorting, fetchCarousels]);

  const updateFilters = useCallback((newFilters) => {
    if (Object.keys(newFilters).length === 0) {
      setFilters({});
    } else {
      setFilters((prev) => ({ ...prev, ...newFilters }));
    }
    setCurrentPage(1);
  }, []);

  const handleTableChange = useCallback((pagination, _tableFilters, sorter) => {
    let order = sorter.order;
    if (order === 'ascend') {
      order = 'asc';
    } else if (order === 'descend') {
      order = 'desc';
    }

    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
    setSorting({
      field: sorter.field || '',
      order: order || '',
    });
  }, []);

  return {
    carousels,
    loading,
    total,
    currentPage,
    pageSize,
    filters,
    sorting,
    companies,
    shoppingCenters,
    loadingCompanies,
    updateFilters,
    handleTableChange,
    deleteCarousel,
    createCarousel,
    updateCarousel,
    fetchCarousel,
  };
};

export default useCarousels;
